import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { History, X } from "lucide-react";
import { dismissHistoryNotice } from "../api/billing";
import { useHistoryGate } from "../hooks/useHistoryGate";
import { FULL_HISTORY_GBP } from "../lib/pricing";
import { AtlasPanel } from "./AtlasPanel";

// ─── Free-plan history notice ─────────────────────────────────────────────────
// The 12-month window is applied silently by every import path, so the only
// way a free user learns their older walks are missing is if we tell them.
// Two surfaces, both driven by useHistoryGate:
//  - HistoryGateLine: a permanent inline line for the exact spots someone goes
//    looking for a missing walk (a source card on Connected sources, the foot
//    of the Activities list). Never dismissible.
//  - HistoryGateCard: the dashboard card, dismissible server-side. Shown once
//    the account has any activities; pointed copy when an import has actually
//    skipped something (billing.historyTruncated).
// Wording rule (Strava API Policy 5.8): Full history is Atlas's matching, never access
// to the source's data - say "matched", not "imported", when selling Pro.
// "Unlock Full history" is the one gold CTA on the card; the line uses a text link.

function yearOf(date: Date | null): string | null {
  return date ? String(date.getFullYear()) : null;
}

export function HistoryGateLine({
  context,
  style,
}: {
  /** "source": a connected provider card. "activities": foot of the list. */
  context: "source" | "activities";
  style?: React.CSSProperties;
}) {
  const gate = useHistoryGate();
  if (!gate.gated) return null;
  const oldestYear = yearOf(gate.oldestSkippedAt);

  const lead =
    context === "source"
      ? `Without Full history, Atlas matches your last ${gate.months} months.`
      : `Activities before ${gate.cutoffLabel} aren't matched without Full history.`;
  const gap =
    gate.truncated && oldestYear
      ? ` Walks going back to ${oldestYear} were left out.`
      : context === "source"
        ? ` Anything before ${gate.cutoffLabel} isn't on the record.`
        : "";

  return (
    <div
      role="note"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        fontSize: 12.5,
        lineHeight: 1.5,
        color: "var(--text-secondary)",
        background: "var(--surface-sunken)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-md)",
        padding: "8px 10px",
        ...style,
      }}
    >
      <History size={14} style={{ flexShrink: 0, marginTop: 2, color: "var(--text-muted)" }} />
      <span>
        {lead}
        {gap}{" "}
        <Link to="/plan" style={{ color: "var(--text-accent)", textDecoration: "none", fontWeight: 600 }}>
          Unlock Full history
        </Link>{" "}
        and Atlas matches everything you have recorded.
      </span>
    </div>
  );
}

export function HistoryGateCard({ hasActivities }: { hasActivities: boolean }) {
  const gate = useHistoryGate();
  const queryClient = useQueryClient();
  const [hidden, setHidden] = React.useState(false);

  const dismissMutation = useMutation({
    mutationFn: dismissHistoryNotice,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["billing"] });
    },
  });

  if (!gate.gated || gate.dismissed || hidden) return null;
  if (!gate.truncated && !hasActivities) return null;

  const oldestYear = yearOf(gate.oldestSkippedAt);
  const body = gate.truncated
    ? `Your record starts on ${gate.cutoffLabel}. Without Full history, Atlas matches the last ${gate.months} months, so ${
        oldestYear ? `walks going back to ${oldestYear}` : "your older walks"
      } were left out and none of their peaks, trails or regions count yet. Nothing you've collected is ever taken away.`
    : `Your record starts on ${gate.cutoffLabel}. Without Full history, Atlas matches the last ${gate.months} months, so anything older isn't on the record yet. Nothing you've collected is ever taken away.`;

  function dismiss() {
    setHidden(true);
    dismissMutation.mutate();
  }

  return (
    <AtlasPanel style={{ position: "relative" }}>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        style={{
          position: "absolute",
          top: 14,
          right: 14,
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "var(--text-muted)",
          padding: 4,
        }}
      >
        <X size={16} />
      </button>

      <p className="eyebrow" style={{ fontSize: 10, marginBottom: 6 }}>Your record</p>
      <h3 style={{ font: "var(--type-h3)", color: "var(--text-primary)", margin: "0 0 6px", paddingRight: 24 }}>
        {gate.truncated ? "Older walks aren't on the record yet" : "Your record covers the last 12 months"}
      </h3>
      <p style={{ fontSize: 13.5, color: "var(--text-secondary)", lineHeight: 1.5, margin: "0 0 14px", maxWidth: 640 }}>
        {body}
      </p>
      <div className="r-wrap" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <Link
          to="/plan"
          style={{
            padding: "8px 16px",
            background: "var(--accent)",
            color: "var(--text-on-gold)",
            borderRadius: "var(--radius-md)",
            fontSize: 13,
            fontWeight: 600,
            textDecoration: "none",
          }}
        >
          Unlock Full history
        </Link>
        <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
          £{FULL_HISTORY_GBP} once, or included with annual Pro. Your full history starts matching as soon as you pay.
        </span>
      </div>
    </AtlasPanel>
  );
}
